import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@mbs-ui/environments/environment';
import { FeedbackService } from '@services/services/feedback.service';
import { hasGlobalAgentOptionsContent } from '@shared/guards/global-agent-options-content.guard';
import { hasNotificationsContent } from '@shared/guards/notifications-content.guard';
import { AbilityService, Action } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { MbsSize } from 'mbs-ui-kit';
import { PlanModalWrapper } from 'mbs-ui-shared/src/lib/wrappers/plan.wrapper';
import { DotSubType, DotType, MainMenuSidePanelType, MenuItemType } from 'mbs-ui-shared@components/app-header/app-header.types';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import Administrator from 'mbs-ui-shared@models/Administrator';
import { ComputerModeAdapter, ComputersMode } from 'mbs-ui-shared@models/Computer';
import { PermissionFlags } from 'mbs-ui-shared@models/PermissionFlags';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { addNew } from 'mbs-ui-shared@models/SidePanelUriParams';
import { LoadRmmGroupTaskWizardService } from 'mbs-ui-shared@modules/schedule-group-action/utility/load-schedule-group-action-wizard.service';
import AccountManager from 'mbs-ui-shared@modules/support-portal/AccountManager';
import { AppEnvStateService } from 'mbs-ui-shared@services/app-env-state.service';
import { ConnectSettingsService } from 'mbs-ui-shared@services/connect.settings.service';
import { PremiumSupportService } from 'mbs-ui-shared@services/premium-support.service';
import { isFontExists } from 'mbs-ui-shared@utils/isFontExists';
import { EntityWizardsService } from 'wizards/services/entity-wizards/entity-wizards.service';
import { getEntityWizardByType } from 'wizards/utils/getEntityWizardByType';
import { appHeaderElementSelectors } from './app-header.constants';

const ticketsKey = 'shared:header.menu.backup.tickets';

@Injectable({ providedIn: 'root' })
export class MainMenu {
  private stateService = inject(AppEnvStateService);
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private i18nPipe = inject(I18NextPipe);
  private connectSettings = inject(ConnectSettingsService);
  private licensesFacade = inject(LicensesFacade);
  private entityWizardsService = inject(EntityWizardsService);
  private feedBackService = inject(FeedbackService);
  private router = inject(Router);
  private planWrapper = inject(PlanModalWrapper);
  private loadRmmGroupTaskWizardService = inject(LoadRmmGroupTaskWizardService);
  private premiumSupportService = inject(PremiumSupportService);

  constructor() {
    this.initStreams();
  }

  private connectClientUrl: string = environment.raDownloadURL.windows;
  private allowRead = (rule: string, action: Action = 'read') => this.ability.can(action, rule);
  private canRead = (rules: string | string[]) => (Array.isArray(rules) ? rules.every((r) => this.allowRead(r)) : this.allowRead(rules));

  private readonly groupIconClassName = 'fa fa-group';
  private readonly userIconClassName = 'fa fa-user';
  private readonly downloadIconClassName = 'fa fa-download';
  private readonly wrenchIconClassName = 'fa fa-wrench';
  private readonly barCharIconClassName = 'fa fa-bar-chart';
  private readonly monitoringIconClassName = 'fa fa-line-chart';
  private readonly globeIconClassName = 'fa fa-globe';
  private readonly certificateIconClassName = 'fa fa-certificate';
  private readonly magicIconClassName = 'fa fa-magic';
  private readonly envelopeIconClassName = 'fa fa-envelope';
  private readonly computerIconClassName = 'fa fa-television';
  private readonly remoteDeployIconClassName = 'fa fa-play-circle-o';
  private readonly restoreToCloudIconClassName = 'fa fa-cloud-upload';
  private readonly settingsIconClassName = 'fa fa-cog';
  private readonly storageIconClassName = 'ico ico-hdd-solid';
  private readonly editIconClassName = 'fa fa-edit';
  private readonly lockIconClassName = 'fa fa-lock';
  private readonly clockIconClassName = 'fa fa-clock-o';

  public readonly elementSelectors = appHeaderElementSelectors;

  public getHelpMenuItems = (
    supportAction: () => void,
    accountManager: AccountManager,
    currentUser: Administrator,
    showBuyPremiumButton = false
  ): MenuItemType[] => {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.help.documentation'),
        id: 'documentation',
        iconClass: 'fa fa-question-circle',
        routeUrl: '/AP/Help/getting-started',
        title: `${this.stateService.appEnvState()?.version}`,
        target: '_blank',
        can: true
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.help.knowledgeBase'),
        id: 'knowledge-base',
        iconClass: 'fa fa-book',
        externalUrl: 'https://kb.msp360.com/',
        title: this.i18nPipe.transform('shared:header.menu.help.knowledgeBase'),
        target: '_blank',
        can: true
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.help.getDemo'),
        id: 'get-demo',
        iconClass: 'fa fa-calendar icon-padding-fix',
        externalUrl: this.prepareDemoUrl(accountManager?.calendarUrl, currentUser),
        dotSubType: DotSubType.New,
        title: this.i18nPipe.transform('shared:header.menu.help.getDemo'),
        target: '_blank',
        can: !!accountManager?.calendarUrl
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.help.feedback'),
        id: 'feedback',
        iconClass: this.envelopeIconClassName,
        externalUrl: 'https://feedback.msp360.com/',
        target: '_blank',
        title: this.i18nPipe.transform('shared:header.menu.help.feedback'),
        can: this.feedBackService.canShowWidgetByPermissions()
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.help.forum'),
        id: 'forum',
        iconClass: 'fa fa-comment',
        externalUrl: 'http://forum.msp360.com',
        title: this.i18nPipe.transform('shared:header.menu.help.forum'),
        can: true
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.help.marketing'),
        id: 'marketing',
        iconClass: 'fa fa-file',
        routeUrl: '/AP/Marketing',
        title: this.i18nPipe.transform('shared:header.menu.help.marketing'),
        can: this.canRead(PermissionsEnum.HelpMarketing)
      },
      this.buildSupportMenuItem(supportAction, showBuyPremiumButton),
      {
        name: this.i18nPipe.transform('shared:products.remote_desktop'),
        id: 'rd',
        iconClass: 'fa fa-desktop',
        action: () => this.openOrDownloadBSP(),
        title: 'Download ' + this.i18nPipe.transform('shared:products.remote_desktop'),
        can: true
      }
    ];
  };

  public getOverviewMenuItems = (): MenuItemType[] => {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.overview.overview'),
        id: 'overview',
        iconClass: 'fa fa-pie-chart',
        routeUrl: RoutingPath.ApDashboard,
        can: this.canRead(PermissionsEnum.Dashboard)
      }
    ];
  };

  public getComputersMenuItems = (): MenuItemType[] => {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.computers.computers'),
        id: 'rm',
        iconClass: this.computerIconClassName,
        routeUrl: this.getComputerUrlByMode(ComputersMode.Computers),
        can: this.canRead(PermissionsEnum.RemoteManagement)
      }
    ];
  };

  public getTicketsMenuItems = (): MenuItemType[] => {
    let ticketsMenu = [];

    if (this.canRead([PermissionsEnum.AllowTicketingSubsystem, PermissionsEnum.ShowTickets])) {
      ticketsMenu = [
        {
          name: this.i18nPipe.transform(ticketsKey),
          id: 'tickets',
          iconClass: 'mbs-agent mbs-agent-sm mbs-agent_tickets',
          routeUrl: '/AP/Tickets',
          can: true
        }
      ];
    }

    return ticketsMenu;
  };

  public getBackupMenuItems = (currentUser: Administrator, hasCloudRestoreLicense: boolean): MenuItemType[] => {
    let backupMenu = [];

    if (
      (this.canRead(PermissionsEnum.Backup) &&
        (this.canRead(PermissionsEnum.RemoteManagement) ||
          this.canRead(PermissionsEnum.Monitoring) ||
          this.canRead(PermissionsEnum.RemoteDeploy) ||
          (this.canRead(PermissionsEnum.CloudRestore) && !this.canRead(PermissionsEnum.B2Only)))) ||
      this.canRead(PermissionsEnum.GoogleApps) ||
      (this.canRead(PermissionsEnum.Storage) && !this.canRead('SolutionUnion')) ||
      this.canRead(PermissionsEnum.Package)
    ) {
      backupMenu = [
        {
          name: this.i18nPipe.transform('shared:header.menu.backup.backup'),
          id: 'rm-backup-parent',
          iconClass: 'mbs-agent mbs-agent-sm mbs-agent_backup',
          can: true,
          children: [
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.computers'),
              id: 'rm-backup',
              iconClass: this.computerIconClassName,
              routeUrl: this.getComputerUrlByMode(ComputersMode.Backup),
              can: this.canRead(PermissionsEnum.RemoteManagement) && this.canRead(PermissionsEnum.Backup)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.monitoring'),
              id: 'monitoring',
              iconClass: this.monitoringIconClassName,
              routeUrl: '/AP/Monitoring',
              can: this.canRead(PermissionsEnum.Monitoring) && this.canRead(PermissionsEnum.Backup)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.backupHistory'),
              id: 'backup-backup-history',
              iconClass: this.editIconClassName,
              routeUrl: RoutingPath.ApBackupHistory,
              additionalRouteUrls: [RoutingPath.ApNewBackupHistory],
              can: this.canRead(PermissionsEnum.BackupHistory) && this.canRead(PermissionsEnum.Backup)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.backupTemplates'),
              id: 'remote-deploy',
              iconClass: this.remoteDeployIconClassName,
              routeUrl: '/AP/BackupTemplates',
              can: this.canRead(PermissionsEnum.RemoteDeploy) && this.canRead(PermissionsEnum.Backup)
            },
            {
              isHorizontalDivider: true,
              name: 'HR1',
              id: 'hr1',
              can:
                this.canRead(PermissionsEnum.RemoteManagement) ||
                (this.canRead(PermissionsEnum.CloudRestore) &&
                  !this.canRead(PermissionsEnum.B2Only) &&
                  this.canRead(PermissionsEnum.Backup) &&
                  (this.canRead(PermissionsEnum.RestoreFromIBBToAzure) ||
                    this.canRead(PermissionsEnum.RestoreFromIBBToEC2) ||
                    hasCloudRestoreLicense))
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.restoreWizard'),
              id: 'restore-wizard',
              iconClass: 'ico ico-Restore',
              action: () => this.openGenericRestoreWizard(),
              can: this.canRead(PermissionsEnum.RemoteManagement)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.restoreToCloud'),
              id: 'restore-to-cloud',
              iconClass: this.restoreToCloudIconClassName,
              routeUrl: RoutingPath.ApRestoreToCloud,
              can:
                this.canRead(PermissionsEnum.CloudRestore) && !this.canRead(PermissionsEnum.B2Only) && this.canRead(PermissionsEnum.Backup)
            },
            {
              isHorizontalDivider: true,
              name: 'HR2',
              id: 'hr2',
              can: this.canRead(PermissionsEnum.GoogleApps)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.m365Google'),
              id: 'gapps',
              iconClass: this.envelopeIconClassName,
              action: () => this.router.navigateByUrl(RoutingPath.ApAppsDomains),
              can: this.canRead(PermissionsEnum.GoogleApps)
            },
            {
              isHorizontalDivider: true,
              name: 'HR3',
              id: 'hr3',
              can: (this.canRead(PermissionsEnum.Storage) && !this.canRead('SolutionUnion')) || this.canRead(PermissionsEnum.Package)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.storageAccounts'),
              id: 'storage-accounts-new',
              iconClass: this.storageIconClassName,
              routeUrl: RoutingPath.ApStorages,
              can: this.canRead(PermissionsEnum.Storage) && !this.canRead('SolutionUnion')
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.storageLimits', { format: 'title' }),
              id: 'storage-limits',
              iconClass: this.storageIconClassName,
              routeUrl: RoutingPath.StorageLimits,
              can: this.canRead(PermissionsEnum.Package)
            },
            {
              name: this.i18nPipe.transform('shared:header.menu.backup.backupStorage'),
              id: 'storage-tree',
              iconClass: this.storageIconClassName,
              routeUrl: RoutingPath.ApBackupStorage,
              can:
                this.canRead(PermissionsEnum.Storage) &&
                !this.canRead('SolutionUnion') &&
                (currentUser?.IsSuperAdmin || currentUser?.ProviderInfo?.IsTest)
            }
          ]
        }
      ];
    }

    return backupMenu;
  };

  public getM365MenuItems = (): MenuItemType[] => {
    let m365Menu = [];

    if (this.canRead(PermissionsEnum.GoogleApps)) {
      m365Menu = [
        {
          name: this.i18nPipe.transform('shared:header.menu.m365Google.m365GoogleBackup'),
          id: 'appsMenu',
          iconClass: `text-primary ${this.envelopeIconClassName}`,
          routeUrl: RoutingPath.ApAppsDomains,
          can: true
        }
      ];
    }

    return m365Menu;
  };

  public getRMMMenuItems = (currentUser: Administrator, rmmTemplates: { id: string; name: string }[]): MenuItemType[] => {
    if (!this.canRead(PermissionsEnum.Rmm)) return [];

    const rmmPermission =
      this.canRead(PermissionsEnum.RmmPerEndpoint) ||
      this.canRead(PermissionsEnum.RmmCeEnabled) ||
      this.canRead(PermissionsEnum.RmmPerAdminLicenseExists);

    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.rmm.rmm'),
        id: 'rm-rmm-parent',
        iconClass: 'mbs-agent mbs-agent-sm mbs-agent_rmm',
        can: true,
        children: [
          this.getRMMComputersMenuWithViews(rmmTemplates),
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.alertHistory'),
            id: 'rmm-alerts-history',
            iconClass: this.barCharIconClassName,
            routeUrl: '/AP/RMMAlertsHistory',
            can: this.canRead(PermissionsEnum.Notification) && rmmPermission
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.alertProfiles'),
            id: 'rmm-notifications',
            iconClass: 'ico ico-alert-profiles',
            routeUrl: '/AP/RMMNotifications',
            can: this.canRead(PermissionsEnum.Notification) && rmmPermission
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.publicApi'),
            id: 'rmm-public-api',
            iconClass: 'ico ico-api-tokens',
            routeUrl: RoutingPath.RMMAPITokens,
            can:
              this.canRead(PermissionsEnum.Notification) &&
              rmmPermission &&
              this.canRead(PermissionsEnum.Provider) &&
              this.canRead(PermissionsEnum.ManageRmmApiTokens)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.newGroupActionTask'),
            id: 'schedule-tasks-new',
            iconClass: 'ico ico-calendar-alt',
            action: () => this.openRMMGroupActionNew(),
            can: rmmPermission
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.groupActionsTasks'),
            id: 'schedule-tasks',
            iconClass: 'ico ico-calendar-alt',
            routeUrl: '/AP/RMMGroupTaskAction',
            can: rmmPermission
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.scriptLibrary'),
            id: 'script-library',
            iconClass: 'fa fa-file-text',
            routeUrl: '/AP/ScriptLibrary',
            can: rmmPermission || currentUser?.IsProvider
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.rmm.snmpMonitoring'),
            id: 'snmp-monitoring',
            iconClass: 'ico ico-snmp',
            routeUrl: '/AP/Snmp',
            can: rmmPermission
          }
        ]
      }
    ];
  };

  public getRepostingMenuItems = (): MenuItemType[] => {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.reporting.reporting'),
        id: 'reporting',
        iconClass: this.barCharIconClassName,
        can: true,
        children: [
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.planReportSettings'),
            id: 'plan-report-settings',
            iconClass: 'fa fa-envelope-square',
            routeUrl: '/AP/PlanReportSettings',
            can: this.canRead(PermissionsEnum.Notification) && this.canRead(PermissionsEnum.Backup)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.scheduledReports'),
            id: 'scheduled-reports-beta',
            iconClass: this.clockIconClassName,
            routeUrl: '/AP/ScheduledReports',
            can: this.canRead(PermissionsEnum.Notification)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.billing'),
            id: 'billing',
            iconClass: 'fa fa-money',
            routeUrl: '/AP/Billing.aspx',
            can: this.canRead(PermissionsEnum.Billing)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.backupHistory'),
            id: 'backup-history',
            iconClass: this.editIconClassName,
            routeUrl: RoutingPath.ApBackupHistory,
            can: this.canRead(PermissionsEnum.BackupHistory) && this.canRead(PermissionsEnum.Backup)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.licenseUsage'),
            id: 'license-usage',
            iconClass: 'fa fa-random',
            routeUrl: '/AP/LicenseUsageHistory.aspx',
            can: this.canRead(PermissionsEnum.LicenseUsageHistory)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.storageUsage'),
            id: 'storage-usage',
            iconClass: this.barCharIconClassName,
            routeUrl: RoutingPath.ApStorageUsage,
            can:
              this.canRead(PermissionsEnum.StorageUsage) &&
              (this.canRead(PermissionsEnum.Backup) || this.canRead(PermissionsEnum.GoogleApps))
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.capacityReport'),
            id: 'capacity-report',
            iconClass: 'fa fa-pie-chart',
            routeUrl: '/AP/StorageCapacityReport',
            can:
              this.canRead(PermissionsEnum.CapacityReport) &&
              (this.canRead(PermissionsEnum.Backup) || this.canRead(PermissionsEnum.GoogleApps))
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.emailsCustomization'),
            id: 'emails-customization',
            iconClass: 'fa fa-edit',
            routeUrl: '/AP/EmailsCustomization',
            can: this.canRead(PermissionsEnum.Notification)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.reporting.connectStatistics'),
            iconClass: this.monitoringIconClassName,
            routeUrl: '/AP/RDStatistics',
            can: this.canRead([PermissionsEnum.RemoteManagement, PermissionsEnum.ShowRemoteConnection]),
            id: 'rd-stats'
          }
        ]
      }
    ];
  };

  public getOrganizationMenuItems = (
    currentUser: Administrator,
    openSidePanelAction: (type: MainMenuSidePanelType) => void
  ): MenuItemType[] => {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.organization.organization'),
        id: 'organization',
        iconClass: this.groupIconClassName,
        can: true,
        children: [
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.users'),
            id: 'users',
            iconClass: 'fa fa-user-circle',
            routeUrl: RoutingPath.ApUsers,
            can: this.canRead(PermissionsEnum.Users)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.licenses'),
            id: 'licenses',
            iconClass: 'fa fa-key',
            routeUrl: RoutingPath.ApLicenses,
            can: this.canRead(PermissionsEnum.Licenses)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.purchaseHistory'),
            id: 'purchase-history',
            iconClass: 'fa fa-credit-card',
            routeUrl: RoutingPath.ApPurchaseHistory,
            can: this.canRead(PermissionsEnum.PurchaseHistory)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.companies'),
            id: 'companies',
            iconClass: this.groupIconClassName,
            routeUrl: RoutingPath.ApCompanies,
            can: this.canRead(PermissionsEnum.ManageCompanies)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.administrators'),
            id: 'administrators',
            iconClass: this.userIconClassName,
            routeUrl: RoutingPath.ApAdministrators,
            can: this.canRead(PermissionsEnum.Administrator)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.organization.auditLog'),
            id: 'audit-log',
            iconClass: 'fa fa-history',
            routeUrl: '/AP/AuditLog',
            can: this.canRead(PermissionsEnum.AuditLog)
          }
        ]
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.settings.settings'),
        id: 'settings',
        iconClass: this.settingsIconClassName,
        dotType: DotType.Settings,
        can: true,
        children: [
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.general'),
            id: 'general',
            iconClass: this.wrenchIconClassName,
            routeUrl: RoutingPath.ApSettings,
            can: this.canRead(PermissionsEnum.Provider)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.ipAllowlisting'),
            id: 'ip-allowlisting',
            iconClass: this.lockIconClassName,
            action: () => openSidePanelAction(MainMenuSidePanelType.IpWhiteList),
            can: currentUser.IsProvider
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.notifications'),
            id: 'notifications',
            iconClass: this.envelopeIconClassName,
            dotType: DotType.Notifications,
            routeUrl: RoutingPath.ApNotifications,
            can: this.canRead(PermissionsEnum.Notification) && hasNotificationsContent(this.ability)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.emailService'),
            id: 'email-service',
            iconClass: this.envelopeIconClassName,
            routeUrl: '/AP/EmailService',
            can: this.canRead(PermissionsEnum.Notification)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.integrations'),
            id: 'integration-systems',
            iconClass: 'ml-1 fa fa-code-fork',
            can: this.canRead(PermissionsEnum.PSA),
            children: [
              {
                name: this.i18nPipe.transform('shared:header.menu.settings.connectWisePSA'),
                id: 'connectWise-manage-new',
                iconClass: 'fa fa-rss',
                routeUrl: '/AP/ConnectWiseNew',
                can: true
              },
              {
                name: this.i18nPipe.transform('shared:header.menu.settings.haloPSA'),
                id: 'halo-psa',
                iconClass: 'ico ico-halo-psa',
                routeUrl: '/AP/HaloPSA',
                can: true
              },
              {
                name: this.i18nPipe.transform('shared:header.menu.settings.autotask'),
                id: 'autotask',
                iconClass: 'fa fa-tasks',
                routeUrl: '/AP/IntegrationAutotask.aspx',
                can: this.canRead(PermissionsEnum.ShowAutotaskIntegration)
              }
            ]
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.mbsTickets'),
            id: 'mbs-tickets-psa',
            iconClass: 'fa fa-ticket',
            routeUrl: '/AP/TicketSettings',
            can: this.canRead([PermissionsEnum.AllowTicketingSubsystem, PermissionsEnum.PSA])
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.onlineAccess'),
            id: 'online-access',
            iconClass: this.globeIconClassName,
            action: () => openSidePanelAction(MainMenuSidePanelType.OnlineAccess),
            can: this.canRead(PermissionsEnum.OnlineAccess)
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.bindingsAndCertificates'),
            id: 'bindings-and-certificates',
            iconClass: this.certificateIconClassName,
            routeUrl: '/AP/SSL',
            can: this.canRead('CustomCertificatesBinding')
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.webConsoleRebranding'),
            id: 'web-console-rebranding',
            iconClass: this.magicIconClassName,
            routeUrl: '/AP/WebConsoleRebranding',
            can: this.canRead('WebConsoleRebranding')
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.branding'),
            id: 'branding',
            iconClass: 'fa fa-star',
            can: this.canRead(PermissionsEnum.Rebranding) && this.canRead(PermissionsEnum.ManageCompanies),
            routeUrl: '/AP/Branding'
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.globalAgentOptions'),
            id: 'global-agent-options',
            iconClass: this.userIconClassName,
            can:
              ((this.canRead(PermissionsEnum.ManageCompanies) && this.canRead(PermissionsEnum.AccessToAllCompanies)) ||
                currentUser.IsProvider) &&
              hasGlobalAgentOptionsContent(this.ability),
            routeUrl: '/AP/GlobalAgentOptions'
          },
          {
            name: this.i18nPipe.transform('shared:header.menu.settings.passwordRecovery'),
            id: 'password-recovery',
            can: currentUser.IsProvider,
            iconClass: this.lockIconClassName,
            action: () => openSidePanelAction(MainMenuSidePanelType.PasswordRecovery)
          }
        ]
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.downloads.downloads'),
        id: 'downloads',
        e2eKey: this.elementSelectors.name?.downloadBtn,
        iconClass: this.downloadIconClassName,
        action: () => openSidePanelAction(MainMenuSidePanelType.Downloads),
        can: this.canRead(PermissionsEnum.Downloads)
      }
    ];
  };

  public getAddMenuItems(
    currentUser: Administrator,
    permissionFlags: PermissionFlags,
    addComputerByMode: () => void,
    openPaymentModal: () => void
  ): MenuItemType[] {
    return [
      {
        name: this.i18nPipe.transform('shared:header.menu.add.computer'),
        id: 'add-computer',
        iconClass: 'fa fa-television',
        action: () => addComputerByMode(),
        can:
          this.ability.can('read', PermissionsEnum.RemoteManagement) &&
          this.ability.can('read', PermissionsEnum.Downloads) &&
          (this.ability.can('read', PermissionsEnum.Backup) ||
            this.ability.can('read', PermissionsEnum.Rmm) ||
            this.ability.can('read', PermissionsEnum.ShowRemoteConnection) ||
            this.auth.isInternalAdmin)
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.add.company'),
        id: 'add-company',
        iconClass: 'fa fa-group',
        action: () => this.addCompany(),
        can:
          this.ability.can('read', PermissionsEnum.ManageCompanies) &&
          (this.ability.can('read', 'Company') || this.ability.can('read', PermissionsEnum.AccessToAllCompanies))
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.add.m365GoogleDomain'),
        id: 'add-apps-domain',
        iconClass: 'fa fa-envelope',
        routeUrl: `${RoutingPath.ApAppsDomains}/${addNew}`,
        can: this.ability.can('read', PermissionsEnum.GoogleApps)
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.add.license'),
        id: 'add-license',
        iconClass: 'fa fa-key',
        action: () => this.addLicense(),
        can: permissionFlags.isShowLicensesDialog
      },
      {
        name: this.i18nPipe.transform('shared:header.menu.add.credit'),
        id: 'add-credit',
        iconClass: 'fa fa-usd',
        action: () => openPaymentModal(), // top up
        can: this.ability.can('read', PermissionsEnum.LicenseBuy) && currentUser.ProviderInfo.IsPostPayment
      }
    ];
  }

  public getRMMComputersMenuWithViews(rmmTemplates: { id: string; name: string }[]): MenuItemType {
    if (rmmTemplates?.length) {
      return {
        name: this.i18nPipe.transform('shared:header.menu.rmm.computers'),
        id: 'rm-rmm',
        iconClass: this.computerIconClassName,
        can: true,
        children: rmmTemplates.map((item) => ({
          name: item.name ?? item.id,
          id: `rmm-page-template-${item.id}`,
          action: () => this.router.navigate([RoutingPath.ApRMMPage], { queryParams: { templateId: item.id } }),
          can: true
        }))
      };
    }

    return {
      name: this.i18nPipe.transform('shared:header.menu.rmm.computers'),
      id: 'rm-rmm',
      iconClass: this.computerIconClassName,
      routeUrl: this.getComputerUrlByMode(ComputersMode.RMM),
      can: true
    };
  }

  // initializations
  private initStreams() {
    this.connectSettings.getConnectClientBuild().subscribe((connectClientUrl) => {
      this.connectClientUrl = connectClientUrl;
    });
    this.licensesFacade.loadTypes(true);
  }

  private getComputerUrlByMode(mode: ComputersMode): string {
    return ComputerModeAdapter.find((modeUrl) => mode === modeUrl.mode)?.url ?? '';
  }

  private buildSupportMenuItem(supportAction: () => void, showBuyPremiumButton: boolean): MenuItemType {
    const hasPremiumSupportLicense = this.premiumSupportService.hasPremiumSupportLicense();

    if (hasPremiumSupportLicense) {
      return {
        name: this.i18nPipe.transform('shared:header.menu.help.premiumSupport'),
        id: 'support',
        iconClass: 'fa fa-life-ring mbs-premium-ring',
        action: () => supportAction(),
        title: this.i18nPipe.transform('shared:header.menu.help.premiumSupport'),
        can: true
      };
    }

    return {
      name: this.i18nPipe.transform('shared:header.menu.help.support'),
      subName: this.i18nPipe.transform('shared:header.menu.help.supportStandard'),
      id: 'support',
      iconClass: 'fa fa-life-ring',
      action: () => supportAction(),
      title: this.i18nPipe.transform('shared:header.menu.help.support'),
      can: true,
      ...(showBuyPremiumButton
        ? {
            actionButton: {
              id: 'buy-premium',
              label: this.i18nPipe.transform('shared:header.menu.help.buyPremium'),
              action: () => this.premiumSupportService.open()
            }
          }
        : {})
    };
  }

  // actions
  private addLicense() {
    this.licensesFacade.proceed();
  }

  private addCompany() {
    this.entityWizardsService.openWizard(getEntityWizardByType(), { size: MbsSize.lg });
  }

  private openOrDownloadBSP() {
    const openBSPLnk = 'cblra://qwer';
    const isFontExist = isFontExists('CloudBerry1');

    if (isFontExist) {
      window.open(openBSPLnk, '_self');
    } else {
      window.open(this.connectClientUrl, '_blank');
    }
  }

  private openRMMGroupActionNew(): void {
    this.loadRmmGroupTaskWizardService.loadWizard().subscribe();
  }

  private prepareDemoUrl(url: string, currentUser: Administrator): string {
    if (!url) return '';

    const urlObj = new URL(url);
    urlObj.searchParams.set('email', currentUser?.Email);
    urlObj.searchParams.set('name', `${currentUser?.FirstName} ${currentUser?.LastName}`);
    return urlObj.href;
  }

  private openGenericRestoreWizard() {
    this.planWrapper.startGenericRestoreWizard();
  }
}
